<template>
  <q-page padding>
    <div class="billing-management">
      <!-- Billing Overview -->
      <div class="row q-col-gutter-md">
        <!-- Billing Stats -->
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">{{ t('billing.overview') }}</div>
            </q-card-section>
            <q-card-section>
              <div class="row q-col-gutter-md">
                <div class="col-12 col-md-3" v-for="stat in billingStats" :key="stat.label">
                  <q-card :class="stat.class">
                    <q-card-section>
                      <div class="text-h6">{{ stat.label }}</div>
                      <div class="text-h4">{{ stat.value }}</div>
                      <div class="text-caption" v-if="stat.caption">
                        <q-icon :name="stat.trendIcon" />
                        {{ stat.caption }}
                      </div>
                    </q-card-section>
                  </q-card>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Payment Methods -->
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">{{ $t('billing.paymentMethods') }}</div>
            </q-card-section>
            <q-list separator>
              <q-item v-for="method in paymentMethods" :key="method.id">
                <q-item-section avatar>
                  <q-icon :name="getPaymentIcon(method.type)" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ method.name }}</q-item-label>
                  <q-item-label caption>{{ method.description }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-toggle
                    v-model="method.enabled"
                    :disable="method.enabled && isOtherMethodEnabled(method.id)"
                    @update:model-value="updateSelectedProcessor(method)"
                  />
                </q-item-section>
              </q-item>
            </q-list>
          </q-card>
        </div>

        <!-- Feature Management -->
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">{{ $t('features.management') }}</div>
            </q-card-section>
            <q-table
              :rows="features"
              :columns="[
                { name: 'name', label: $t('features.name'), align: 'left', field: 'name' },
                { name: 'enabled', label: $t('features.enabled'), align: 'center', field: 'enabled' },
              ]"
              row-key="id"
            >
              <template v-slot:body-cell-enabled="props">
                <q-toggle
                  v-model="props.row.enabled"
                  @update:model-value="(value) => toggleFeature(props.row.id, value)"
                />
              </template>
            </q-table>
          </q-card>
        </div>

        <!-- Subscription Plans -->
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">{{ $t('billing.subscriptionPlans') }}</div>
            </q-card-section>
            <q-table :rows="subscriptionPlans" :columns="planColumns" row-key="id">
              <template v-slot:body-cell-status="props">
                <q-td :props="props">
                  <q-chip :color="getPlanStatusColor(props.value)" text-color="white">
                    {{ props.value }}
                  </q-chip>
                </q-td>
              </template>
            </q-table>
          </q-card>
        </div>

        <!-- Feature Management -->
        <div class="col-12">
          <q-card>
            <q-card-section>
              <div class="text-h6">{{ t('features.management') }}</div>
            </q-card-section>
            <q-table
              :rows="features"
              :columns="[
                { name: 'name', label: t('features.name'), align: 'left', field: 'name' },
                { name: 'enabled', label: t('features.enabled'), align: 'center', field: 'enabled' },
              ]"
              row-key="id"
            >
              <template v-slot:body-cell-enabled="props">
                <q-toggle
                  v-model="props.row.enabled"
                  @update:model-value="(value) => toggleFeature(props.row.id, value)"
                />
              </template>
            </q-table>
            <q-banner v-if="error" type="warning">
              {{ error }}
            </q-banner>
          </q-card>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useQuasar } from 'quasar';
import { usePaymentStore } from '../stores/paymentStore';
import { getClientFeatures, updateClientFeatures } from '../services/featureService';
import { initializeSinchClient } from '../services/sinchClient';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../stores/auth';

const { t } = useI18n();
const $q = useQuasar();
const paymentStore = usePaymentStore();
const authStore = useAuthStore();

// Reactive variables
const subscriptionPlans = ref([]);
const recentTransactions = ref([]);
const features = ref([]);
const loading = ref(false);

const billingStats = ref([
  { label: t('billing.monthlyRevenue'), value: '$45,250', class: 'bg-primary text-white', caption: `${t('billing.changeFromLastMonth')} 12%`, trendIcon: 'trending_up' },
  { label: t('billing.overdueInvoices'), value: 5, class: 'bg-negative text-white', caption: `$2,500 ${t('billing.totalOverdue')}` },
  { label: t('billing.activeSubscriptions'), value: 125, class: 'bg-positive text-white', caption: `15 ${t('billing.onTrial')}` },
  { label: t('billing.churnRate'), value: '2.5%', class: 'bg-warning text-white', caption: t('billing.last30Days') },
]);

const paymentMethods = ref([
  { id: 1, type: 'stripe', name: 'Stripe', description: 'Secure credit card payments', enabled: true },
  { id: 2, type: 'paypal', name: 'PayPal', description: 'PayPal Express Checkout', enabled: false },
]);

// Get the current user from the auth store
const currentUser = computed(() => authStore.user);

const planColumns = [
  { name: 'name', label: 'Plan Name', field: 'name' },
  { name: 'price', label: 'Price', field: 'price', format: (val) => `$${val.toFixed(2)}` },
  { name: 'status', label: 'Status', field: 'status' },
];

// Toggle feature state
const toggleFeature = async (featureId, isEnabled) => {
  try {
    await updateClientFeatures(featureId, isEnabled, true);
    $q.notify({ type: 'positive', message: t('features.updateSuccess') });
  } catch (error) {
    console.error('Error updating feature:', error);
    $q.notify({ type: 'negative', message: t('features.updateError') });
  }
};

// Fetch features
const fetchFeatures = async () => {
  loading.value = true;
  error.value = null;
  try {
    features.value = await getClientFeatures(true); // Use Sinch backend
  } catch (error) {
    console.error('Error fetching features:', error);
    $q.notify({ type: 'negative', message: t('features.fetchError') });
  } finally {
    loading.value = false;
  }
};

// Setup Sinch client
const setupSinch = async () => {
  try {
    const sinchClient = await initializeSinchClient(currentUser.value.id);

    await sinchClient.startActiveConnection({
      username: currentUser.value.username,
      password: currentUser.value.token, // Dynamic token
    });

    authStore.sinchClient = sinchClient; // Save to auth store
  } catch (err) {
    console.error('Sinch setup failed:', err);
    $q.notify({ type: 'negative', message: t('billing.sinchSetupError') });
  }
};

const togglePaymentMethod = (method) => {
  paymentMethods.value.forEach((m) => (m.enabled = m.id === method.id));
  paymentStore.setProcessor(method.type);
  $q.notify({ message: `${method.name} enabled`, color: 'positive' });
};

const getPaymentIcon = (type) => (type === 'stripe' ? 'credit_card' : 'payment');

const getPlanStatusColor = (status) => {
  return status === 'Active' ? 'positive' : 'negative';
};

const isOtherMethodEnabled = (id) => {
  return paymentMethods.value.some((method) => method.enabled && method.id !== id);
};

// Fetch billing data on mount
onMounted(async () => {
  loading.value = true;

  try {
    await fetchFeatures();
    recentTransactions.value = await paymentStore.fetchPayments();
    subscriptionPlans.value = await paymentStore.fetchSubscriptions();
  } catch (err) {
    console.error('Error loading billing data:', err);
    $q.notify({ type: 'negative', message: t('billing.fetchError') });
  } finally {
    loading.value = false;
  }
});

const updateSelectedProcessor = (method) => {
  paymentMethods.value.forEach((m) => {
    m.enabled = m.id === method.id; // Only one processor can be active
  });
  paymentStore.setProcessor(method.type); // Update the selected processor dynamically
  $q.notify({ message: `${method.name} selected as the payment processor.`, color: 'positive' });
};
</script>

<style scoped>
.billing-management {
  max-width: 1200px;
  margin: auto;
}
</style>